import { UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "context/auth-context/auth.context";
import { useSecurity } from "context/security-context/security.context";
import { useContext } from "react";
import {
  getClients,
  getClientsById,
  getTemplates,
  updateClient,
  updateFeature,
} from "services/client.service";
import { DataFeatures, IClient, SELECT_DESIGN, SELECT_FEATURES } from "types/client.type";
import { EFeatures } from "types/features.type";
import RolesType from "types/roles.type";
import {
  deleteImage,
  getImages,
  uploadImage,
  deployBuild,
  deploySubmit,
  deployAssociate,
  deployUpdateMetadata,
  IDeployBuildPayload,
  IDeploySubmitPayload,
  IDeployAssociatePayload,
  IDeployUpdateMetadataPayload,
} from "../../services/client.service";

export interface IUseUpdateClient {
  select: string;
}
export interface IUseUpdateFeatureClient {
  feature: EFeatures;
  dataFeature: DataFeatures;
  select?: string;
}

export enum KeyQueryClient {
  getById = "getClientByIds",
  getClients = "getClients",
  patchClient = "patchClient",
  post = "postClient",
  getClientsTemplates = "getClientsTemplates",
  getClientsFeatures = "getClientsFeatures",
}

export const useGetClientById = (select: string): UseQueryResult<IClient> => {
  const { clientId } = useContext(AuthContext);

  return useQuery({
    queryKey: [KeyQueryClient.getById, clientId, select],
    queryFn: async () => {
      return getClientsById(clientId, select);
    },
    enabled: !!clientId,
  });
};

export const useGetClients = (): UseQueryResult<IClient[]> => {
  const { currentAdmin } = useContext(AuthContext);
  const { isSuperAdmin } = useSecurity();

  return useQuery({
    queryKey: [KeyQueryClient.getClients],
    queryFn: () => {
      return isSuperAdmin ? getClients() : Promise.resolve([]);
    },
    enabled: !!isSuperAdmin,
  });
};

export const useGetTemplate = () => {
  return useQuery({
    queryKey: [KeyQueryClient.getClientsTemplates],
    queryFn: () => getTemplates(),
  });
};

export const useUpdateClient = ({ select }: IUseUpdateClient) => {
  const queryClient = useQueryClient();
  const { clientId } = useContext(AuthContext);

  return useMutation({
    mutationFn: (data: IClient) => {
      const formData = new FormData();
      //if (data.logo) formData.append("file", data.logo);
      if (data.logo) formData.append("logo", data.logo);
      if (data.longDesc) formData.append("longDesc", data.longDesc);
      if (data.name) formData.append("name", data.name);
      if (data.shortDesc) formData.append("shortDesc", data.shortDesc);
      return updateClient(clientId, formData, select);
    },
    onSuccess: (result) => {
      queryClient.setQueryData([KeyQueryClient.getById, clientId, select], result);
      const currentDesign: IClient = queryClient.getQueryData([
        KeyQueryClient.getById,
        clientId,
        SELECT_DESIGN,
      ]);
      if (currentDesign) {
        queryClient.setQueryData([KeyQueryClient.getById, clientId, SELECT_DESIGN], {
          ...currentDesign,
          name: result.name,
          logo: result.logo,
        });
      }
    },
  });
};

export const useUpdateFeatureClient = () => {
  const queryClient = useQueryClient();
  const { clientId } = useContext(AuthContext);

  return useMutation({
    mutationFn: async ({ feature, dataFeature, select }: IUseUpdateFeatureClient) => {
      return updateFeature(clientId, feature, dataFeature, select);
    },
    onSuccess: (result) => {
      queryClient.setQueryData(
        [KeyQueryClient.getClientsFeatures, clientId, SELECT_FEATURES],
        result
      );
      const currentClient: IClient = queryClient.getQueryData([
        KeyQueryClient.getById,
        clientId,
        SELECT_DESIGN,
      ]);
      if (currentClient) {
        queryClient.setQueryData([KeyQueryClient.getClientsFeatures, clientId, SELECT_FEATURES], {
          ...currentClient,
          features: result.features,
        });
      }
    },
  });
};

export const useGetImages = () => {
  const { clientId } = useContext(AuthContext);
  return useQuery({
    queryKey: ["images", clientId],
    queryFn: () => getImages(clientId),
    enabled: !!clientId,
  });
};

export const useUploadImage = () => {
  const queryClient = useQueryClient();
  const { clientId } = useContext(AuthContext);

  return useMutation({
    mutationFn: (formData: FormData) => uploadImage(clientId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["images", clientId] });
    },
  });
};

export const useDeleteImage = () => {
  const queryClient = useQueryClient();
  const { clientId } = useContext(AuthContext);

  return useMutation({
    mutationFn: ({imageUrl, mediaId}: {imageUrl: string, mediaId: string}) => deleteImage(clientId, imageUrl, mediaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["images", clientId] });
    },
  });
};

// Deployment hooks

export const useDeployBuild = (targetClientId?: string) => {
  const { clientId: contextClientId } = useContext(AuthContext);
  const clientId = targetClientId || contextClientId;

  return useMutation({
    mutationFn: (payload: IDeployBuildPayload) => deployBuild(clientId, payload),
  });
};

export const useDeploySubmit = (targetClientId?: string) => {
  const { clientId: contextClientId } = useContext(AuthContext);
  const clientId = targetClientId || contextClientId;

  return useMutation({
    mutationFn: (payload: IDeploySubmitPayload) => deploySubmit(clientId, payload),
  });
};

export const useDeployAssociate = (targetClientId?: string) => {
  const { clientId: contextClientId } = useContext(AuthContext);
  const clientId = targetClientId || contextClientId;

  return useMutation({
    mutationFn: (payload: IDeployAssociatePayload) => deployAssociate(clientId, payload),
  });
};

export const useDeployUpdateMetadata = (targetClientId?: string) => {
  const { clientId: contextClientId } = useContext(AuthContext);
  const clientId = targetClientId || contextClientId;

  return useMutation({
    mutationFn: (payload: IDeployUpdateMetadataPayload) => deployUpdateMetadata(clientId, payload),
  });
};
